import { ChangeEvent, useState } from "react"
import { css } from "@emotion/css"
import { useI18n } from "providers/I18n"
import { useHistory } from "react-router-dom"

import Button from "ui/buttons/Button"
import * as DG from "ui/dialogs"
import FormControl from "@material-ui/core/FormControl"
import { useMutation, queryCache } from "react-query"

import { TextInput } from "components/inputs/TextInput"
import { createMarketAgency } from "api/agencies"
import { ApiError, ErrorsMap } from "api/http/utils"

const styles = {
  inputWrapper: css`
    max-width: 400px;
    margin-right: auto;
  `,
  fieldGroup: css`
    padding-bottom: 0px;
  `,
  nameTextfield: css`
    margin-top: var(--spacing-xxs);
    width: 256px;
  `,
  codeTextfield: css`
    width: 158px;
  `,
  dialog: css`
    max-width: 551px;
    margin: auto;
  `,
}

export type CreateAgencyProps = {
  open: boolean
  onClose: () => void
}

export type AgencyPayload = {
  name: string
  code: string
}

function CreateAgency(props: CreateAgencyProps) {
  const { open, onClose } = props
  const history = useHistory()
  const { t } = useI18n()

  const InitialState = { name: "", code: "" }
  const [state, setState] = useState<AgencyPayload>(InitialState)
  const [errors, setErrors] = useState<ErrorsMap | undefined>()

  const handleChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = evt.target
    !!name && setState((s) => ({ ...s, [name]: value }))
  }

  const [create] = useMutation(createMarketAgency, {
    onSuccess: () => {
      onClose()
      queryCache.invalidateQueries("market-agencies")
      history.push(`/market-agencies`)
    },
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
    },
  })

  const handleSubmit = () => {
    const pld = {
      name: state?.name,
      code: state?.code,
    }
    create(pld).catch((e) => {})
  }

  const id = "create-new-agency"
  return (
    <DG.Dialog
      className={styles.dialog}
      open={open}
      onClose={onClose}
      id={id}
      disableEscapeKeyDown
      keepMounted
    >
      <DG.DialogHeader>
        <DG.DialogTitle id={id}>{t("agency.create.title")}</DG.DialogTitle>
        <DG.DialogClose onClose={onClose} />
      </DG.DialogHeader>

      <DG.DialogContent paddingTop>
        <div className={styles.inputWrapper}>
          <FormControl variant="outlined">
            <div className={styles.fieldGroup}>
              <TextInput
                fullWidth
                className={styles.nameTextfield}
                name="name"
                label={t("agency.input.name")}
                value={state?.name}
                margin="normal"
                size="medium"
                onChange={handleChange}
                error={!!errors && !!errors["name"]}
                errorMessage={!!errors ? errors["name"] : ""}
              />
            </div>
            <div className={styles.fieldGroup}>
              <TextInput
                name="code"
                value={state?.code}
                label={t("agency.input.code")}
                margin="normal"
                size="medium"
                onChange={handleChange}
                error={!!errors && !!errors["code"]}
                errorMessage={!!errors ? errors["code"] : ""}
              />
            </div>
          </FormControl>
        </div>
      </DG.DialogContent>
      <DG.DialogFooter>
        <DG.DialogActions>
          <Button variant="outlined" color="primary" onClick={onClose}>
            {t("agency.button.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            onClick={handleSubmit}
            disabled={state.name === "" || state.code === ""}
          >
            {t("agency.button.create")}
          </Button>
        </DG.DialogActions>
      </DG.DialogFooter>
    </DG.Dialog>
  )
}

export default CreateAgency
